import { router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { TagInput } from '@/components/ui/TagInput'
import { useRouteParams } from '@/hooks/useRoutes'
import type { CannedAnswerRow } from './CannedAnswersList'

interface CannedAnswerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  answer?: CannedAnswerRow | null
}

export function CannedAnswerDialog({ open, onOpenChange, answer }: CannedAnswerDialogProps) {
  const { withAccount } = useRouteParams()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  // Reset form state when the dialog opens for a different answer.
  useEffect(() => {
    if (!open) return
    setTitle(answer?.title ?? '')
    setBody(answer?.body ?? '')
    setTags(answer?.tags ?? [])
  }, [open, answer])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    const payload = {
      canned_answer: {
        title,
        body,
        tags,
        status: 'active'
      }
    }
    const accountId = withAccount().account_id
    const finish = { onSuccess: () => onOpenChange(false), onFinish: () => setSaving(false), preserveScroll: true }
    if (answer) {
      router.patch(`/${accountId}/shortcuts/${answer.id}`, payload, finish)
    } else {
      router.post(`/${accountId}/shortcuts`, payload, finish)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{answer ? 'Edit canned answer' : 'New canned answer'}</DialogTitle>
          <DialogDescription>
            Quick reply template. Use <code className="font-mono text-xs px-1 py-0.5 bg-muted rounded">{`{{name}}`}</code> placeholders for parts that change.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ca-title">Title</Label>
            <Input
              id="ca-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Merge duplicate profiles"
              autoFocus
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ca-body">Body</Label>
            <textarea
              id="ca-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              required
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              placeholder="Ah, I see — I can merge the 2 {{profile_url}} — Sound good?"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ca-tags">Tags</Label>
            <TagInput id="ca-tags" value={tags} onChange={setTags} placeholder="profiles, merge…" />
            <p className="text-xs text-muted-foreground">Used to rank chips against visitor message keywords.</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="cursor-pointer">Cancel</Button>
            <Button type="submit" disabled={saving || !title || !body} className="cursor-pointer">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
